import type { ReactNode } from 'react';
import { SplitPane } from '../components/SplitPane.js';
import { cn } from '../lib/cn.js';
import { INSPECTOR_MAX_WIDTH, INSPECTOR_MIN_WIDTH } from './Inspector.js';
import { RAIL_MAX_WIDTH, RAIL_MIN_WIDTH } from './LeftRail.js';

export interface ShellLayout {
  railWidth: number;
  railCollapsed: boolean;
  inspectorWidth: number;
  inspectorOpen: boolean;
  bottomHeight: number;
  bottomOpen: boolean;
}

export const DEFAULT_SHELL_LAYOUT: ShellLayout = {
  railWidth: 272,
  railCollapsed: false,
  inspectorWidth: 360,
  inspectorOpen: false,
  bottomHeight: 220,
  bottomOpen: false,
};

export interface AppShellProps {
  /** LeftRail element; receives width via layout. */
  rail: ReactNode;
  topBar?: ReactNode;
  tabs?: ReactNode;
  content: ReactNode;
  inspector?: ReactNode;
  /** BottomPanel element (it manages its own open/height through layout callbacks). */
  bottomPanel?: ReactNode;
  layout: ShellLayout;
  /** Called with the full next layout on any resize; persist it. */
  onLayoutChange: (next: ShellLayout) => void;
  className?: string;
}

/**
 * Workbench composition (spec section 22): rail 220–340 (or 56 collapsed) | top bar h-12 | tabs |
 * content with optional 320–480 inspector | bottom panel. Nothing scrolls except the content and
 * the panes themselves. Sizes flow through `layout` so the host persists them.
 */
export function AppShell({ rail, topBar, tabs, content, inspector, bottomPanel, layout, onLayoutChange, className }: AppShellProps) {
  const set = (patch: Partial<ShellLayout>) => onLayoutChange({ ...layout, ...patch });
  const main = (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {topBar}
      {tabs}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="min-h-0 min-w-0 flex-1">
          {inspector ? (
            <SplitPane
              direction="horizontal"
              primary="second"
              size={layout.inspectorWidth}
              onResize={(w) => set({ inspectorWidth: w })}
              min={INSPECTOR_MIN_WIDTH}
              max={INSPECTOR_MAX_WIDTH}
              collapsed={!layout.inspectorOpen}
              label="Resize inspector"
              className="h-full"
            >
              {content}
              {inspector}
            </SplitPane>
          ) : (
            <div className="h-full min-h-0 min-w-0">{content}</div>
          )}
        </div>
        {bottomPanel}
      </div>
    </div>
  );
  return (
    <div className={cn('flex h-screen w-screen overflow-hidden bg-bg text-ink', className)}>
      <SplitPane
        direction="horizontal"
        primary="first"
        size={layout.railCollapsed ? 56 : layout.railWidth}
        onResize={(w) => {
          if (layout.railCollapsed) return;
          set({ railWidth: w });
        }}
        min={layout.railCollapsed ? 56 : RAIL_MIN_WIDTH}
        max={layout.railCollapsed ? 56 : RAIL_MAX_WIDTH}
        label="Resize navigation rail"
        className="h-full w-full"
      >
        {rail}
        {main}
      </SplitPane>
    </div>
  );
}
